import Button from '@/components/atoms/button/button';
import Dropdown from '@/components/atoms/dropdown/dropdown';
import GenericToast from '@/components/atoms/generic-toast/genericToast';
import GlobalContext from '@/contexts/globalContext';
import UseTraceItemShipping from '@/hooks/useTraceItemShipping';
import UseVerificationDigit from '@/hooks/useVerificationDigit';
import { getCompanyName } from '@/services/getCompanyNameService';
import { normalizeWords } from '@/utils/normalizeWords';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import CryptoJS from 'crypto-js';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useContext, useEffect, useState } from 'react';
import Cookies from 'universal-cookie';
import { DocumentType } from '../../../enums/enums';
import NotBusinessModal from '../not-bussiness-modal/notBussinessModal';
import './businessModal.scss';

interface BusinessModalInterface {
    closeModal?: () => void;
    isMobile?: boolean;
    setShowConfirmation?: (boolean) => void;
    isPrivate?: boolean;
    closeModalPermissionPage?: () => void;
    serviceId?: string;
}

const BusinessModal = (props: Partial<BusinessModalInterface> = {}) => {
    const {
        closeModal = () => {},
        isMobile = false,
        setShowConfirmation = () => {},
        isPrivate = false,
        closeModalPermissionPage,
        serviceId,
    } = props;
    const data = useStaticQuery(graphql`
        query GET_PORTAL_SERVICIOS_BUSINESS_MODAL {
            allContentfulTemplate(
                filter: {
                    titulo: {
                        eq: "Modal Portal > Accede a tus servicios empresariales"
                    }
                }
            ) {
                nodes {
                    nombre
                    imagen {
                        title
                        file {
                            url
                        }
                        gatsbyImageData(
                            formats: [AUTO, WEBP]
                            placeholder: BLURRED
                        )
                    }
                    referencias {
                        ... on ContentfulTemplate {
                            nombre
                            descripcion {
                                raw
                            }
                            redireccion
                            copyBoton
                        }
                    }
                }
            }
        }
    `);
    const cookies = new Cookies();

    const getCleanBusinessList = (originalBusiness: any) => {
        const businessDocs = originalBusiness?.map(
            (business: any, index: number) => ({
                ...business,
                label: `${
                    DocumentType[business.companyDocType as keyof DocumentType]
                } ${business.companyDocID}`,
                value: index,
            })
        );

        const businessMap = new Map<string, any>();
        const newList: any[] = [];

        businessDocs?.forEach((item) => {
            const existing = businessMap.get(item.companyDocID);

            if (existing) {
                existing.serviceId.push(item.serviceId);
            } else {
                businessMap.set(item.companyDocID, {
                    ...item,
                    serviceId: [item.serviceId],
                });
            }
        });

        businessMap.forEach((item) => {
            if (!serviceId || item?.serviceId?.includes(serviceId)) {
                newList.push(item);
            }
        });

        return newList;
    };

    const [selectedValue, setSelectedValue] = useState<any>();
    const [showMessage, setShowMessage] = useState<any>(false);
    const { business, gigyaContext, userInfoContext } =
        useContext(GlobalContext);
    const [loader, setLoader] = useState(false);
    const [options, setOptions] = useState<any>([]);
    const { imagen, nombre, referencias } = data.allContentfulTemplate.nodes[0];

    useEffect(() => {
        setTimeout(() => {
            setShowMessage(false);
        }, 3000);
    }, [showMessage]);

    useEffect(() => {
        if (
            !(gigyaContext.gigyaAccount && options.length === 0 && !isPrivate)
        ) {
            document.documentElement.style.overflowY = 'hidden';
            return () => {
                document.documentElement.style.overflowY = 'scroll';
                setTimeout(() => {
                    document.documentElement.style.overflowY = 'scroll';
                }, 2000);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setModalClose = () => {
        closeModal();
    };

    const handleTrace = (message: string) => {
        if (gigyaContext?.gigyaAccount) {
            UseTraceItemShipping(
                gigyaContext?.gigyaAccount?.profile.firstName +
                    ' ' +
                    (gigyaContext.gigyaAccount.data.secondName ?? ''
                        ? `${gigyaContext.gigyaAccount.data.secondName} `
                        : '') +
                    gigyaContext.gigyaAccount.profile.lastName +
                    (gigyaContext.gigyaAccount.data.secondLastName ?? ''),
                gigyaContext?.gigyaAccount?.data.docNumber,
                gigyaContext?.gigyaAccount?.data.docType,
                gigyaContext?.gigyaAccount?.profile.email,
                'click',
                `${normalizeWords(message)}`,
                userInfoContext.data?.userJwt
            );
        }
    };

    const setValueClose = async () => {
        if (!selectedValue) {
            return;
        }
        setLoader(true);
        const docToSend =
            selectedValue.companyDocID.replace(/\s/g, '') +
            (selectedValue.companyDocType === 'CO1N'
                ? UseVerificationDigit(String(selectedValue.companyDocID))
                : '');

        try {
            const getBusinessName = await getCompanyName(docToSend);
            if (
                getBusinessName?.status === 200 &&
                getBusinessName?.data?.records?.length > 0
            ) {
                if (getBusinessName?.data?.records[0].status === 'VIGEN') {
                    selectedValue.label =
                        getBusinessName?.data?.records[0].companyName;
                    business?.set(selectedValue);
                    cookies.set(
                        'CURRENT_BUSINESS',
                        CryptoJS.AES.encrypt(
                            JSON.stringify(selectedValue),
                            'comfama'
                        ).toString(),
                        {
                            path: '/',
                            expires: new Date(
                                new Date(Date.now() + 60 * 10000)
                            ),
                        }
                    );
                    handleTrace('Seleccionar Empresa exitoso');
                    setShowConfirmation(
                        <GenericToast
                            setActiveToast={setShowConfirmation}
                            isOk={true}
                            message="Cambio de empresa exitoso"
                            position="up-right"
                        />
                    );
                    setModalClose();
                } else {
                    handleTrace('Seleccionar Empresa fallido');
                    setShowMessage(
                        getBusinessName?.data?.records[0]?.status === 'VIGEN'
                            ? 'No fue posible cambiar de empresa'
                            : 'La empresa no se encuentra vigente'
                    );
                }
            } else {
                setShowMessage('No fue posible cambiar de empresa');
            }
        } catch (error) {
            console.error(error);
        }
        setLoader(false);
    };

    const setOptionsClass = (className: string) => {
        return {
            renderNode: {
                [BLOCKS.PARAGRAPH]: (_node: any, children: any) => (
                    <p className={className}>{children}</p>
                ),
            },
        };
    };

    useEffect(() => {
        setOptions(
            getCleanBusinessList(
                (gigyaContext.gigyaAccount?.data as any)?.servicesAuth
            ) ?? []
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gigyaContext.gigyaAccount?.data, serviceId]);

    if (gigyaContext.gigyaAccount && options.length === 0) {
        return (
            <NotBusinessModal
                closeModal={() => {
                    if (closeModalPermissionPage) {
                        closeModalPermissionPage();
                    } else {
                        closeModal();
                    }
                }}
                isMobile={isMobile}
            />
        );
    }

    if (!gigyaContext.gigyaAccount) {
        document.documentElement.style.overflowY = 'scroll';
        return null;
    }

    return (
        <div
            className={
                'm-business-modal ' +
                (options.length <= 0 ? ' __not-visible' : '')
            }
        >
            <div className="m-business-modal__content" onClick={() => {}}>
                <div className="m-business-modal__exit">
                    <i
                        className="icon icon-close m-business-modal__exit-icon"
                        onClick={setModalClose}
                    />
                </div>
                <div className="m-business-modal__container">
                    {imagen?.[0]?.gatsbyImageData ? (
                        <GatsbyImage
                            loading="eager"
                            className="m-business-modal__container-icon"
                            image={imagen[0].gatsbyImageData}
                            alt={imagen[0].title}
                            title={imagen[0].title}
                        />
                    ) : (
                        imagen[0].file?.url && (
                            <img
                                src={imagen[0].file.url}
                                alt={imagen[0].title}
                                title={imagen[0].title}
                                className="m-business-modal__container-icon"
                            />
                        )
                    )}
                    <h3 className="m-business-modal__container-title">
                        {nombre}
                    </h3>
                    {documentToReactComponents(
                        JSON.parse(referencias[0].descripcion.raw),
                        setOptionsClass(
                            'm-business-modal__container-description'
                        )
                    )}
                    <Dropdown
                        selectedValue={selectedValue}
                        setSelectedValue={setSelectedValue}
                        placeholder={'Selecciona o escribe'}
                        options={options}
                    />
                    {showMessage && (
                        <div className="m-business-modal__container-message">
                            <i className="icon icon-exclamation-triangle m-business-modal__container-message__icon" />
                            <p className="m-business-modal__container-message__text">
                                {showMessage}
                            </p>
                        </div>
                    )}
                    <Button
                        type="button"
                        classname="m-business-modal__container-button modal"
                        text={referencias[3].copyBoton}
                        isDisabled={!selectedValue}
                        onClickAction={setValueClose}
                        loader={loader}
                    />
                </div>
            </div>
            <div className="m-business-modal__bkg" onClick={setModalClose} />
        </div>
    );
};

export default BusinessModal;
